/** @jsxImportSource ./simboluri-jsx */
import { useCallback, useEffect, useRef, useState } from "react";

import { RezistentaResult, StepState, fmtDate } from "./tipuri";
import { stepDrawingUrl } from "./apiStructura";
import AvertismenteMetoda from "./AvertismenteMetoda";
import { DrawingView } from "./DwgViewer";
import Marime, { EtichetaNatura, FormulaMarime } from "./Marime";
import { BaraFataDeLimita, Pas } from "./Explica";
import { DescriereaPasului } from "./TitluPas";
import StepRezistentaVerificari, { Focus, FocusTab } from "./StepRezistentaVerificari";
// `Focus` e deja luat de focusul INTERN al paginii (tab + ax); cel din URL,
// scris de linkurile verificărilor picate, intră sub alt nume
import { Focus as FocusLink } from "./legaturi";

import DeciziiPas from "./DeciziiPas";
import { bucatiMarcate, t, tn, tx, useT } from "../i18n";
// Fundația ca pagină — servește AMÂNDOI pașii ei, fiindcă amândoi descriu
// aceeași geometrie, doar la momente diferite:
//
//   Pasul 6  predimensionarea: presiune vs praguri, tasare, K_s, volumul în
//            viewer — pe o greutate ESTIMATĂ, întinsă uniform pe talpă;
//   Pasul 17 aceleași cifre, dar pe geometria ADOPTATĂ după verificarea ax
//            cu ax pe reacțiunile reale — plus tabloul acelor verificări.
//
// Diferența pe ecran e una singură (blocul `StepRezistentaVerificari`), deci
// două componente ar fi însemnat două copii ale celorlalte douăzeci de
// carduri. Cheia pasului decide de unde se citește desenul și care descriere
// din `catalog_pasi.py` apare când pasul n-a rulat.

function Check({ ok }: { ok: boolean }) {
  return <span className={ok ? "st-ok" : "st-bad"}>{ok ? "✓" : "✗"}</span>;
}

export default function StepRezistenta({
  projectId,
  cloneId = null,
  state,
  running,
  focus,
  pas = "rezistenta_fundatie",
}: {
  projectId: string;
  // clona soluției vizualizate din care se citesc datele; null = proiectul
  cloneId?: string | null;
  state: StepState | null;
  running: boolean;
  // axul/stâlpul cerut dintr-un link (?focus=Ax%204) — v. legaturi.ts
  focus?: FocusLink;
  // care dintre cei doi pași ai fundației se arată (v. comentariul de sus)
  pas?: "rezistenta_fundatie" | "verificare_fundatie";
}) {
  useT();
  const eVerificare = pas === "verificare_fundatie";
  if (running) {
    return (
      <div className="st-status">
        <div className="st-spinner" />
        <div>
          <strong>{t("struct.rz.pasul_ruleaza")}</strong>
          <div className="sg-muted">
            {eVerificare
              ? t("struct.rz.ruleaza_verificare")
              : t("struct.rz.ruleaza_predim")}
          </div>
        </div>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="sg-muted st-empty">
        {t("struct.pas.nerulat")}
        {/* descrierea vine din catalog_pasi.py — același text cu cel de la
            hover pe titlul pasului, ca să nu existe două versiuni */}
        <DescriereaPasului cheie={pas} />
      </div>
    );
  }

  const r = state.result as unknown as RezistentaResult;

  if (state.status === "error") {
    return (
      <div>
        <div className="sg-error">{t("struct.pas.esuat", { eroare: r?.error ?? t("struct.pas.eroare_necunoscuta") })}</div>
        <div className="sg-muted">{t("struct.rz.ruleaza_nou_meniul")}</div>
      </div>
    );
  }

  return <RezistentaView projectId={projectId} cloneId={cloneId} r={r}
                         updatedAt={state.updated_at} focusLink={focus}
                         pas={pas} />;
}

// Vederea rezultatului, separată ca să poată ține starea de NAVIGARE (ce ax e
// selectat, ce tab e deschis) — partajată între indicatorul de verificări,
// tabelele Pasului 17 și desenul 2D, ca „jump to" să funcționeze în ambele
// sensuri.
function RezistentaView({
  projectId,
  cloneId = null,
  r,
  updatedAt,
  focusLink,
  pas,
}: {
  projectId: string;
  // clona soluției vizualizate din care se citesc desenul și CSV-ul
  cloneId?: string | null;
  r: RezistentaResult;
  updatedAt: string;
  focusLink?: FocusLink;
  pas: "rezistenta_fundatie" | "verificare_fundatie";
}) {
  useT();
  const eVerificare = pas === "verificare_fundatie";
  // deschiderea de CALCUL a secțiunii globale — a axului cel mai puțin
  // solicitat, nu maximul pe clădire. Pe rezultatele de dinainte
  // câmpul lipsește, deci se cade pe maxim și pagina rămâne cum era.
  const L0baza = r.L0_baza_m ?? r.L0_max_m;
  const f = r.fundare;
  // lățimile DISTINCTE de talpă din proiect (maximum 3, v. F9): de când
  // fiecare ax se dimensionează din încărcarea lui, „B" nu mai e un număr
  const latimi = Array.from(new Set([
    r.latime_m,
    ...Object.values(r.latime_pe_ax ?? {}),
  ].map((b) => Math.round(Number(b) * 100) / 100))).sort((a, b) => a - b);
  // Presiunea care CHIAR dimensionează: maximul pe axe din Pasul 17. Cardul
  // arăta până acum media Pasului 6 (84 kPa pe proiectul 17), în timp ce tabelul
  // de dedesubt avea axe la 325 kPa — patru ori mai mult. Cine se uita doar la
  // card credea că fundația are o rezervă de 270 %.
  const axe = r.verificari?.axe ?? [];
  const pAx = axe
    .map((a) => Math.max(a.p_max_kpa ?? 0, a.p_max_model_kpa ?? 0))
    .filter((x) => x > 0);
  const pMaxAx = pAx.length ? Math.round(Math.max(...pAx) * 10) / 10 : null;
  const axMaxim = pMaxAx == null ? null
    : axe.find((a) => Math.max(a.p_max_kpa ?? 0, a.p_max_model_kpa ?? 0) >= pMaxAx - 0.05)?.nume ?? null;

  // valorile reale ale proiectului, pentru placeholderele din formulele
  // catalogului (`{G}`, `{p_conv}` …) — v. catalog_marimi.py
  const valoriPef = {
    G: r.greutate_kn, L: r.lungime_m, B: r.latime_m,
    p_ef: r.presiune_efectiva_kpa,
  };
  const valoriG = {
    g_mp: r.greutate_t_mp, A_desf: r.suprafata_desfasurata_m2, G: r.greutate_t,
  };
  const valoriPraguri = {
    p_conv: r.p_conv_kpa,
    p_static: r.presiune_maxima_statica_kpa ?? r.prag_static_kpa,
    p_dinamic: r.presiune_maxima_dinamica_kpa ?? r.prag_dinamic_kpa,
  };

  const [focus, setFocus] = useState<Focus>({ tab: "axe", ref: null, n: 0 });
  const verificariRef = useRef<HTMLDivElement>(null);
  const desenRef = useRef<HTMLDivElement>(null);

  // jump-to: schimbă tabul + elementul selectat și derulează la secțiune
  const sari = useCallback((tab: FocusTab, ref: string | null,
                           unde: "verificari" | "desen" = "verificari") => {
    setFocus((f0) => ({ tab, ref, n: f0.n + 1 }));
    const el = (unde === "desen" ? desenRef : verificariRef).current;
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  // linkul dintr-o verificare picată a Pasului 19 aduce numele axului („Ax 4")
  // sau id-ul stâlpului: același drum ca butonul „vezi verificările axului"
  useEffect(() => {
    if (focusLink?.ref) sari("axe", focusLink.ref);
  }, [focusLink?.n, focusLink?.ref, sari]);

  return (
    <div className="st-result">
      {/* REZULTATUL ÎNTÂI: cifrele pe care le duci în
          desenul de cofraj stau în capul paginii; explicațiile de metodă vin
          după ele și sunt strânse implicit. */}
      <div className="st-findings">
        <h3>{t("struct.rz.fundatia_propusa")}</h3>
        <div className="st-finding">
          <span className="st-finding-label">{t("insp.grup.geometrie")}</span>
          <div>
            L = {r.lungime_m} m · <strong>B = {r.latime_m} m · Df = {r.adancime_m} m</strong>
            <span className="sg-muted">
              {t("struct.rz.sapatura_strat", { v: r.volum_fundatie_m3, strat: r.strat_fundare ?? "—" })}
            </span>
            {/* explicațiile celor trei dimensiuni vin din catalogul de
                mărimi — aceleași cuvinte ca pe cardul de mai jos și ca la
                Pasul 5, în loc de un bloc narativ scris local */}
            <span className="st-marimi">
              <Marime cheie="L" valori={{ L: r.lungime_m }} />
              <Marime cheie="B" valori={{ B: r.latime_m }} />
              <Marime cheie="Df" valori={{ Df: r.adancime_m }} />
            </span>
          </div>
        </div>
        {r.h_grinda_m != null && (
          <div className="st-finding">
            <span className="st-finding-label">
              {t("struct.fel.fundatie")}
              <Marime cheie="H" />
            </span>
            <div>
              <strong>H = {r.h_grinda_m} m</strong>
              {r.h_talpa_m != null && (
                <>{t("struct.rz.talpa_inima", {
                  B: r.latime_m, h_t: r.h_talpa_m, b_i: r.b_inima_m,
                  h_i: ((r.h_grinda_m ?? 0) - (r.h_talpa_m ?? 0)).toFixed(2),
                })}</>
              )}
              {r.rigiditate_Hc_L0 != null && (
                <> · H/L₀ = {r.rigiditate_Hc_L0} ({r.rigida ? t("struct.rz.rigida") : t("struct.rz.flexibila")})</>
              )}
              <div className="sg-muted">
                {tx("struct.rz.marime_derivata")}
              </div>
              <Pas titlu={t("struct.rz.unde_vine_h", { p1: r.h_grinda_m })}>
                <p>{tx("struct.rz.fundatia_continua")}</p>
                {/* Deschiderea de CALCUL a secțiunii globale e a axului cel
                    mai puțin solicitat, nu maximul pe clădire:
                    altfel o singură travee lungă impunea întregii clădiri
                    grinda și săpătura ei. Axele cu travee mari au cota și
                    secțiunea LOR, în tabelul de mai jos. */}
                <div className="ex-calc">
                  {t("struct.rz.calc_l0", { L0: L0baza, L0_max: r.L0_max_m })}
                  {"\n"}
                  {t("struct.rz.calc_interval", {
                    a: L0baza ? (L0baza / 6).toFixed(2) : "—",
                    b: L0baza ? (L0baza / 3).toFixed(2) : "—",
                  })}{"\n"}
                  {t("struct.rz.calc_marginea_jos", {
                    v: L0baza ? Math.max(L0baza / 6, 0.5).toFixed(2) : "—",
                  })}
                  {"\n"}{t("struct.rz.calc_cota", { v: (r.adancime_m - 0.05).toFixed(2) })}
                  {"\n"}{t("struct.rz.calc_adoptat", { v: r.h_grinda_m })}
                </div>
                {L0baza != null && r.h_grinda_m != null
                  && r.h_grinda_m < Math.max(L0baza / 6, 0.5) - 0.005 && (
                  Math.max(L0baza / 6, 0.5) > r.adancime_m - 0.05 ? (
                    <p>{tx("struct.rz.hc_retezat", { L0: L0baza })}</p>
                  ) : (
                    <p className="st-warn">
                      {tx("struct.rz.hc_sub_l0", {
                        h: r.h_grinda_m,
                        l6: Math.max(L0baza / 6, 0.5).toFixed(2),
                        df: (r.adancime_m - 0.05).toFixed(2),
                      })}
                    </p>
                  )
                )}
                {r.rigiditate_Hc_L0 != null && (
                  <p>
                    {tx(r.rigida ? "struct.rz.consecinta_rigida" : "struct.rz.consecinta_flexibila", {
                      h: r.h_grinda_m, L0: L0baza, r: r.rigiditate_Hc_L0,
                    })}
                  </p>
                )}
              </Pas>
              <div className="sg-muted">{r.h_grinda_sursa}</div>
              <p style={{ margin: "8px 0 4px", fontSize: 13 }}>
                {tx("struct.rz.verif_predim")}
              </p>
              {(r.verificari_sectiune ?? []).map((s, i) => (
                <div key={`${s.cod}-${s.unde ?? i}`}
                     className={s.ok || s.informativ ? "sg-muted" : "st-warn"}>
                  {s.informativ ? "·" : s.ok ? "✓" : "⚠"}{" "}
                  <strong>{s.cod}</strong> {s.nume}: {s.valoare ?? "—"}
                  {s.unitate ? ` ${s.unitate}` : ""}
                  {s.limita != null && <>{t("struct.rz.limita_paranteza", { limita: s.limita })}</>} — {s.referinta}
                  {s.nota && <> · {s.nota}</>}
                </div>
              ))}
              {/* cotele de fundare per ax: câte trepte au ieșit, care ax stă
                  pe care și ce criteriu a impus-o. Fără tabelul ăsta,
                  «adancime_m» pare cota întregii clădiri — și nu mai e
                  . */}
              {r.adancimi_pe_ax?.activa && (
                <>
                  <p style={{ margin: "10px 0 4px", fontSize: 13 }}>
                    <strong>{t("struct.rz.cotele_fundare_ax")}</strong> —{" "}
                    {(r.adancimi_pe_ax.nivele_m ?? [])
                      .map((v) => `${v.toFixed(2)} m`)
                      .join(" / ")}{" "}
                    ({tn("struct.rz.trepte_cel_mult#n", (r.adancimi_pe_ax.nivele_m ?? []).length, {
                      max: r.adancimi_pe_ax.max_nivele ?? 3,
                    })}). {r.adancimi_pe_ax.nota}
                  </p>
                  <table className="sg-table">
                    <thead>
                      <tr>
                        <th>{t("struct.rzv.th_ax")}</th>
                        <th>L₀</th>
                        <th>{t("struct.rz.th_h_necesar")}</th>
                        <th>{t("struct.rz.th_df_necesar")}</th>
                        <th>{t("struct.rz.th_df_adoptat")}</th>
                        <th>{t("struct.rz.impune")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(r.adancimi_pe_ax.per_ax ?? []).map((a) => (
                        <tr key={a.ax}>
                          <td><b>{a.ax}</b></td>
                          <td className="sg-mono">{a.L0_m.toFixed(2)} m</td>
                          <td className="sg-mono">{a.H_nec_m.toFixed(2)} m</td>
                          <td className="sg-mono">
                            {a.Df_necesar_m.toFixed(2)} m
                          </td>
                          <td className="sg-mono">
                            {a.Df_ales_m.toFixed(2)} m
                            {(a.din_cuantizare_m ?? 0) > 0.005 && (
                              <span className="sg-muted">
                                {t("struct.rz.din_cuantizare", { v: a.din_cuantizare_m!.toFixed(2) })}
                              </span>
                            )}
                          </td>
                          <td className="sg-muted">
                            {a.criteriu === "sectiune"
                              ? t("struct.rz.criteriu_sectiune")
                              : t("struct.rz.criteriu_terenul")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>
        )}
        <div className="st-finding">
          <span className="st-finding-label">{t("struct.rz.optimizare")}</span>
          <div>
            {r.optimizat
              ? t("struct.rz.geometria_minima_b", { p1: r.latime_minima_m, p2: r.adancime_minima_m })
              : t("struct.rz.minimul_legal_b", { p1: r.latime_minima_m, p2: r.adancime_minima_m })}
            <span className="sg-muted">{t("struct.rz.presiune_neta", { p: r.p_net_kpa })}</span>
            {/* «cum se caută perechea (B, D_f)» stă în catalogul de mărimi,
                la `optimizare_bd` — o citești de pe cardul dimensiunilor */}
          </div>
        </div>
        {r.avertismente.length > 0 && (
          <div className="st-finding">
            <span className="st-finding-label">{t("struct.note.titlu")}</span>
            <div>
              {r.avertismente.map((w, i) => (
                <div key={i} className="st-warn">⚠ {w}</div>
              ))}
            </div>
          </div>
        )}
        <div className="sg-muted st-ran-at">{t("struct.pas.rulat_la", { cand: fmtDate(updatedAt) })}</div>
      </div>
      {/* lățimea/adâncimea impuse din preferințe și cota de teren presupusă
          sunt DECIZII ale acestui pas — se citesc lângă dimensiunile pe care
          le-au produs (registrul central s-a desființat) */}
      <DeciziiPas decizii={r.decizii} titlu="struct.decizii.pas6" />
      {/* «Ce face pasul acesta» NU mai e o secțiune în pagină: stă la hover pe
          titlul pasului, din `catalog_pasi.py`, la fel ca la ceilalți 18 pași
          (pagina asta avea două secțiuni narative și
          cinci blocuri desfășurabile înainte de prima cifră). */}

      {/* Rezultatul pasului, înaintea oricărei verificări: CE fundație iese.
          Lățimea și adâncimea sunt singurele două cifre pe care proiectantul le
          duce mai departe în desenul de cofraj, deci stau primele și mari. */}
      <div className="st-findings">
        {/* Explicația detaliului („de ce contează, nu doar B și D_f", cele
            trei variante cu G4-ul și costul lor) a intrat în catalogul de
            mărimi, la cheia `detaliu_fundatie`: se citește la hover, lângă
            cifrele la care se referă, în loc să ocupe jumătate de ecran
            înainte de ele. */}
        {r.sectiune_fundatie && (
          <div className="st-finding">
            <span className="st-finding-label">
              {t("struct.rz.sectiunea_aleasa")}
              <Marime cheie="detaliu_fundatie" />
            </span>
            <div>
              <strong>{r.sectiune_descriere}</strong>
              <div className="ex-calc">
                {t("struct.rz.sect_beton_armat", { v: r.sectiune_fundatie.v_beton_armat_m3_ml })}
                {r.sectiune_fundatie.v_beton_simplu_m3_ml
                  ? t("struct.rz.sect_beton_simplu", { v: r.sectiune_fundatie.v_beton_simplu_m3_ml }) : ""}
                {t("struct.rz.pamant_p1_m3", { p1: r.sectiune_fundatie.v_umplutura_m3_ml })}
                {t("struct.rz.sect_cofraj", { v: r.sectiune_fundatie.cofraj_m2_ml })}
                {t("struct.rz.g4_beton_25", { p1: r.sectiune_fundatie.q_kn_ml })}
                {`A = ${r.sectiune_fundatie.A_m2} m²   I_y = ${r.sectiune_fundatie.Iy_m4} m⁴`}
              </div>
              <div className="sg-muted">
                {tx("struct.rz.sect_nota")}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Secțiunea narativă „Ce caută optimizarea și ce rezultă determinist"
          a intrat în catalogul de mărimi, la cheia `optimizare_bd`: se
          citește la hover pe cardul dimensiunilor, unde stau chiar B și D_f.
          Ce a rămas în pagină e derivarea CU CIFRELE proiectului, care nu
          poate sta în catalog fiindcă e diferită de la proiect la proiect. */}
      <div className="st-findings st-derivate">
        <h3>
          {t("struct.rz.marimi_derivate")}
          <Marime cheie="optimizare_bd" />
        </h3>
        <div className="ex-calc">
          {t("struct.rz.formula_h_nec")}
          {`\u00abT\u00bb    H = D_f \u2212 h_peste            = ${r.adancime_m.toFixed(2)} \u2212 ${(r.h_peste_m ?? 0.3).toFixed(2)} = ${(r.h_grinda_m ?? 0).toFixed(2)} m\n`}
          {`h_t   = max(0,30 ; (${r.latime_m.toFixed(2)} \u2212 ${(r.b_inima_m ?? 0.3).toFixed(2)})/2 ; ${(0.3 * r.latime_m).toFixed(2)}) = ${(r.h_talpa_m ?? 0).toFixed(2)} m\n`}
          {`H/L\u2080  = ${(r.h_grinda_m ?? 0).toFixed(2)}/${r.L0_max_m ?? "\u2014"} = ${r.rigiditate_Hc_L0 ?? "\u2014"}  ${r.rigida ? t("struct.rz.formula_rigida") : t("struct.rz.formula_flexibila")}`}
        </div>
      </div>

      <div className="st-cards">
        <div className="st-card st-card-main st-card-lat">
          <div className="st-card-label">
            {t("struct.rz.dimensiunile_fundatiei")}
            <Marime cheie="B" />
            <Marime cheie="Df" />
            <Marime cheie="H" />
          </div>
          {/* Lățimea NU mai e un singur număr de când fiecare ax se
              dimensionează din încărcarea lui: aici se arată setul de lățimi
              distincte (maximum 3 pe proiect), iar volumul e cel REAL,
              însumat pe axe. „B" unic rămâne doar o medie orientativă. */}
          <div className="st-card-value st-card-geom">
            <span>
              B ={" "}
              <strong>
                {latimi.length > 1
                  ? latimi.map((b) => b.toFixed(2)).join(" / ")
                  : r.latime_m.toFixed(2)}
              </strong>{" "}
              <span className="st-small">m</span>
            </span>
            <span className="st-geom-sep">×</span>
            <span>
              D<sub>f</sub> = <strong>{r.adancime_m.toFixed(2)}</strong> <span className="st-small">m</span>
            </span>
          </div>
          <div className="st-card-sub">
            {latimi.length > 1 ? (
              <>
                {tx("struct.rz.latimi_talpa", { n: latimi.length })}
                {r.lungime_pe_latime_m && (
                  <>
                    {" "}{t("struct.rz.repartitia", {
                      lista: Object.entries(r.lungime_pe_latime_m)
                        .map(([b, l]) => t("struct.rz.p1_m_p2", { p1: Number(b).toFixed(2), p2: l }))
                        .join(", "),
                    })}
                  </>
                )}
              </>
            ) : (
              <>
                {tx("struct.rz.doua_variabile")}
              </>
            )}
            {t("struct.rz.l_cumulat", { L: r.lungime_m })}
            {t("struct.rz.sapatura", { v: r.volum_fundatie_m3 })}
            {latimi.length > 1 && r.latime_medie_m != null && (
              <>
                {t("struct.rz.latime_medie", { b: r.latime_medie_m.toFixed(2) })}
              </>
            )}
            <br />
            {tx("struct.rz.sectiune_detaliul")}{" "}
            <code className="st-chip">{r.detaliu_fundatie ?? "—"}</code>):{" "}
            {r.sectiune_descriere
              ?? (r.h_grinda_m != null ? `H = ${r.h_grinda_m} m` : "—")}
            {latimi.length > 1 && (
              <>{t("struct.rz.descrisa_pentru", { b: r.latime_m.toFixed(2) })}</>
            )}
            <br />
            {t("struct.rz.fundare_in", { strat: r.strat_fundare ?? t("struct.rz.stratul_cota_df") })}
          </div>
        </div>
        {/* PRESIUNEA — cele două carduri stau unul lângă altul și, până acum,
            nu se putea spune care e apăsarea și care e limita fără să treci cu
            mouse-ul peste ele. Acum fiecare își poartă
            natura scrisă pe el, iar cardul de solicitare arată și cât din
            limită e consumat. */}
        <div className="st-card st-card-main">
          <div className="st-card-label">
            {t("struct.rz.presiune_teren")} <Check ok={r.presiune_ok} />
            <EtichetaNatura cheie="p_ef" />
            <Marime cheie="p_ef" valori={valoriPef} />
          </div>
          <div className="st-card-value">
            {pMaxAx ?? r.presiune_efectiva_kpa} <span className="st-small">kPa</span>
          </div>
          <div className="st-card-sub">
            {pMaxAx != null ? (
              <>
                {axMaxim
                  ? tx("struct.rz.presiunea_calcul_ax", { ax: axMaxim })
                  : tx("struct.rz.presiunea_calcul")}
                <BaraFataDeLimita valoare={pMaxAx} limita={r.prag_dinamic_kpa}
                                  unitate="kPa" eticheta={t("struct.rz.limita_seismica")} />
                <div className="sg-muted">
                  {t("struct.rz.media_pas6", { p: r.presiune_efectiva_kpa })}
                </div>
              </>
            ) : (
              <>
                {tx("struct.rz.medie_estimata")}
                <BaraFataDeLimita valoare={r.presiune_efectiva_kpa}
                                  limita={r.prag_static_kpa} unitate="kPa" />
              </>
            )}
          </div>
        </div>
        <div className="st-card st-card-main">
          <div className="st-card-label">
            {t("struct.rz.cat_duce_terenul")}
            <EtichetaNatura cheie="praguri" />
            <Marime cheie="praguri" valori={valoriPraguri} />
          </div>
          <div className="st-card-value">
            {r.presiune_maxima_statica_kpa ?? r.prag_static_kpa} <span className="st-small">kPa</span>
          </div>
          <div className="st-card-sub">
            {tx("struct.rz.limita_exploatare_formula")}
            <div>
              {tx("struct.rz.la_cutremur", { p: r.presiune_maxima_dinamica_kpa ?? r.prag_dinamic_kpa })}
            </div>
            <div className="sg-muted">
              {t("struct.rz.p_conv_corectat", { p: r.p_conv_kpa, B: r.latime_m, D: r.adancime_m })}
            </div>
          </div>
        </div>
        <div className="st-card st-card-main">
          <div className="st-card-label">
            {t("struct.rz.tasare_estimata")} <Check ok={r.tasare_ok} />
            <EtichetaNatura cheie="tasare" />
            <Marime cheie="tasare" valori={{ s: r.tasare_cm }} />
          </div>
          <div className="st-card-value">{r.tasare_cm} <span className="st-small">cm</span></div>
          <div className="st-card-sub">
            {tx("struct.rz.valoarea_calculata_limita", { l: r.tasare_maxima_cm })}
            <BaraFataDeLimita valoare={r.tasare_cm} limita={r.tasare_maxima_cm}
                              unitate="cm" />
            <div className="sg-muted">
              {t("struct.rz.zona_activa", { z: r.zona_activa_m })}
            </div>
          </div>
        </div>
        <div className="st-card">
          <div className="st-card-label">
            {t("struct.rz.greutate_cladire")}
            <EtichetaNatura cheie="G" />
            <Marime cheie="G"
                    valori={{ g_mp: r.greutate_t_mp,
                              A_desf: r.suprafata_desfasurata_m2,
                              G: r.greutate_t }} />
          </div>
          <div className="st-card-value">{r.greutate_t} <span className="st-small">t</span></div>
          <div className="st-card-sub">
            {t("struct.rz.t_mp_desfasurat", { g: r.greutate_t_mp, A: r.suprafata_desfasurata_m2 })}
          </div>
        </div>
        <div className="st-card">
          <div className="st-card-label">
            {t("struct.rz.coeficient_pat")}
            <EtichetaNatura cheie="ks" />
            <Marime cheie="ks"
                    valori={{ p_ef: r.presiune_efectiva_kpa, s: r.tasare_cm,
                              ks: r.ks_static_dan_cm3 }} />
          </div>
          <div className="st-card-value">
            {r.ks_static_dan_cm3 ?? "—"} <span className="st-small">daN/cm³</span>
          </div>
          <div className="st-card-sub">
            {t("struct.rz.ks_dinamic", { ks: r.ks_dinamic_dan_cm3 ?? "—", c: r.coef_dinamic })}
          </div>
        </div>
      </div>


      <div className="st-findings">
        <h3>{t("struct.rz.formulele_pasului")}</h3>
        <div className="st-formulas">
          <div className="st-formula">
            <span className="st-formula-what">
              {t("struct.rz.f_greutatea")}
              <Marime cheie="G" valori={valoriG} />
            </span>
            <FormulaMarime cheie="G" valori={valoriG} />
            {r.greutate_kn != null && <> (= {r.greutate_kn} kN)</>}
          </div>
          <div className="st-formula">
            <span className="st-formula-what">
              {t("struct.rz.f_presiunea_efectiva")}
              <EtichetaNatura cheie="p_ef" />
              <Marime cheie="p_ef" valori={valoriPef} />
            </span>
            <FormulaMarime cheie="p_ef" valori={valoriPef} />
          </div>
          <div className="st-formula">
            <span className="st-formula-what">
              {t("struct.rz.f_cat_duce", { B: r.latime_m, Df: r.adancime_m })}
              <EtichetaNatura cheie="praguri" />
              <Marime cheie="praguri" valori={valoriPraguri} />
            </span>
            {f?.p_conv_baza_kpa != null && (
              <><FormulaMarime cheie="p_conv" valori={{
                  p_bar: f.p_conv_baza_kpa, cd: f.cd_kpa, cb: f.cb_kpa,
                  p_conv: r.p_conv_kpa }} /><br /></>
            )}
            <FormulaMarime cheie="praguri" valori={valoriPraguri} />
            {" "}→ p_ef {r.presiune_ok ? "≤" : ">"} p_max,static <Check ok={r.presiune_ok} />
          </div>
          <div className="st-formula">
            <span className="st-formula-what">
              {t("struct.rz.f_presiunea_neta")}
              <EtichetaNatura cheie="p_net" />
              <Marime cheie="p_net" valori={{ p_ef: r.presiune_efectiva_kpa,
                                              gamma: r.gamma_kn_m3,
                                              Df: r.adancime_m,
                                              p_net: r.p_net_kpa }} />
            </span>
            p_net = p_ef − 0,8 · γ · Df = {r.presiune_efectiva_kpa} − 0,8 · {r.gamma_kn_m3 ?? "…"} · {r.adancime_m} = <strong>{r.p_net_kpa} kPa</strong>
          </div>
          <div className="st-formula">
            <span className="st-formula-what">
              {t("struct.rz.f_apasarea")}
              <Marime cheie="sigma_z" />
            </span>
            {tx("struct.rz.f_sigma_z", { z: r.zona_activa_m })}
          </div>
          <div className="st-formula">
            <span className="st-formula-what">
              {t("struct.rz.f_tasarea")}
              <Marime cheie="tasare" />
            </span>
            {tx("struct.rz.f_s", { s: r.tasare_cm, rel: r.tasare_ok ? "≤" : ">", max: r.tasare_maxima_cm })} <Check ok={r.tasare_ok} />
          </div>
          <div className="st-formula">
            <span className="st-formula-what">
              {t("struct.rz.f_coeficientul_pat")}
              <Marime cheie="ks" />
            </span>
            Ks,static = p_ef / s = <strong>{r.ks_static_dan_cm3 ?? "—"} daN/cm³</strong>
            {" "}· Ks,dinamic = Ks,static · {r.coef_dinamic} = <strong>{r.ks_dinamic_dan_cm3 ?? "—"} daN/cm³</strong>
            <div className="st-formula-nota">
              {r.ks_din_presiuni_reale ? (
                <>
                  {tx("struct.rz.ks_reale", {
                    n: r.ks_din_presiuni_reale.nr_axe,
                    min: r.ks_din_presiuni_reale.ks_min_dan_cm3,
                    max: r.ks_din_presiuni_reale.ks_max_dan_cm3,
                  })}
                  {r.ks_estimare_dan_cm3 != null && (
                    <>{" "}{tx("struct.rz.ks_estimare", {
                      ks: r.ks_estimare_dan_cm3,
                      dif: r.ks_static_dan_cm3
                        ? `${Math.round((r.ks_estimare_dan_cm3 / r.ks_static_dan_cm3 - 1) * 100)} %`
                        : "—",
                    })}</>
                  )}
                </>
              ) : (
                <>
                  {tx("struct.rz.ks_estimat")}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* tabloul verificărilor e al Pasului 17: pe Pasul 6 nu există încă
          reacțiuni, deci n-ar avea ce arăta */}
      {eVerificare && (
        <div ref={verificariRef}>
          <StepRezistentaVerificari projectId={projectId} cloneId={cloneId} r={r}
                                    focus={focus} sari={sari} />
        </div>
      )}

      {eVerificare && r.verificari?.stare === "calculat"
        && (r.verificari.avertismente_metoda ?? []).length > 0 && (
        <AvertismenteMetoda avertismente={r.verificari.avertismente_metoda ?? []} />
      )}

      <div className="st-preview-legenda sg-muted" ref={desenRef}>
        {/* cele două cuvinte îngroșate ale frazei sunt, în ordine, culoarea
            fundației și cea a selecției — fiecare cu clasa ei */}
        {(() => {
          let k = 0;
          return bucatiMarcate(t("struct.rz.legenda_desen", { B: r.latime_m })).map((b, i) =>
            b.marcaj === "b"
              ? <strong key={i} className={k++ === 0 ? "st-legenda-galben" : "st-legenda-selectie"}>{b.text}</strong>
              : <span key={i}>{b.text}</span>);
        })()}
      </div>
      <div className="st-preview">
        <DrawingView
          src={stepDrawingUrl(projectId, pas, undefined, cloneId)}
          title={t("struct.rz.fundatie_b_p1", { p1: r.latime_m, p2: r.adancime_m })}
          embed
          // axele din desen poartă numele din tabel („Ax 4"), deci id-ul
          // entității selectate e direct referința de navigare
          focusId={focus.tab === "axe" ? focus.ref : null}
          linkEticheta={t("struct.rz.vezi_verificarile_axului")}
          // saltul la tabel se face DOAR din butonul panoului: clickul pe desen
          // doar selectează (colorează albastru), ca vederea să nu fugă
          onJump={(id, e) => {
            const et = e && e.t !== "n" ? e.et : null;
            if (id && (et === "FOUNDATION_AXIS" || et === "AXIS_ID")) {
              sari("axe", id);
            }
          }}
        />
      </div>
    </div>
  );
}
